import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, Printer, MapPin, Send, HelpCircle, Users, AlertTriangle, CheckCircle } from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import { useTranslation } from "react-i18next";
import "leaflet/dist/leaflet.css";

// Hardcoding the production URL to ensure it's correct
const BACKEND_URL = "https://sbg-backend.onrender.com";

const Contact = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    inquiryType: "",
    message: "",
    terms: false,
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formStatus, setFormStatus] = useState({
    submitted: false,
    success: false,
    message: "",
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({ ...prevState, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormStatus({
      submitted: false,
      success: false,
      message: "",
    });

    try {
      // Using the new dedicated endpoint
      const endpoint = "/api/contact";
      console.log("Submitting inquiry to URL:", BACKEND_URL + endpoint);
      
      const response = await fetch(`${BACKEND_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => 
          ({ message: `Server returned ${response.status}: ${response.statusText}` })
        );
        throw new Error(errorData.message || 'Failed to submit inquiry');
      }

      setFormStatus({
        submitted: true,
        success: true,
        message: "Your inquiry has been submitted successfully. We will get back to you shortly.",
      });
      
      // Clear the form
      setFormData({
        name: "",
        email: "",
        phone: "",
        subject: "",
        inquiryType: "",
        message: "",
        terms: false,
      });
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      setFormStatus({
        submitted: true,
        success: false,
        message: `Failed to submit inquiry: ${error.message}`,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-b from-white to-accent/5">
      <main className="flex-grow p-8 max-w-7xl mx-auto w-full">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4 text-gray-900">
            {t('contact.title')}
          </h1>
          <p className="text-xl text-accent-700 mb-4">
            {t('contact.subtitle')}
          </p>
          <div className="inline-flex space-x-4">
            <Link
              to="/employer-contact"
              className="bg-primary text-white px-6 py-3 rounded-lg hover:bg-primary-dark transition-colors inline-flex items-center shadow-lg hover:shadow-xl group"
            >
              <Users className="mr-2 group-hover:scale-110 transition-transform" />
              {t('contact.forEmployers')}
            </Link>
            <Link
              to="/contact"
              className="bg-accent text-white px-6 py-3 rounded-lg hover:bg-accent-dark transition-colors inline-flex items-center shadow-lg hover:shadow-xl group"
            >
              <HelpCircle className="mr-2 group-hover:scale-110 transition-transform" />
              {t('contact.generalInquiries')}
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className="bg-white p-8 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300">
            <div className="flex items-center mb-6">
              <HelpCircle className="w-8 h-8 text-primary mr-4" />
              <h2 className="text-2xl font-semibold text-gray-900">
                {t('contact.generalInfo.title')}
              </h2>
            </div>
            <p className="mb-6 text-accent-700">
              {t('contact.generalInfo.description')}
            </p>
            <div className="space-y-4">
              <div className="flex items-start">
                <MapPin className="mr-3 mt-1 flex-shrink-0 text-primary" />
                <span className="text-accent-800">
                  4600 East Washington Street
                  <br />
                  Suite 300
                  <br />
                  Phoenix, AZ 85034
                </span>
              </div>
              <div className="flex items-center">
                <Phone className="mr-3 flex-shrink-0 text-primary" />
                <span className="text-accent-800">602-400-8028</span>
              </div>
              <div className="flex items-center">
                <Printer className="mr-3 flex-shrink-0 text-primary" />
                <span className="text-accent-800">Fax: 623-321-1327</span>
              </div>
              <div className="flex items-center">
                <Mail className="mr-3 flex-shrink-0 text-primary" />
                <span className="text-accent-800">
                  info@scottbiz.net
                </span>
              </div>
            </div>
            <div className="mt-8 h-64 rounded-lg overflow-hidden shadow-md">
              <MapContainer
                center={[33.4484, -112.074]}
                zoom={13}
                style={{ height: "100%", width: "100%" }}
              >
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <Marker position={[33.4484, -112.074]}>
                  <Popup>Scott Business Group</Popup>
                </Marker>
              </MapContainer>
            </div>
          </div>

          <div className="bg-white p-8 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300">
            <div className="flex items-center mb-6">
              <Send className="w-8 h-8 text-primary mr-4" />
              <h2 className="text-2xl font-semibold text-gray-900">
                {t('contact.form.sendMessage')}
              </h2>
            </div>
            
            {formStatus.submitted ? (
              <div className={`p-4 rounded-md mb-6 ${
                formStatus.success ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
              }`}>
                <div className="flex items-center mb-2">
                  {formStatus.success ? (
                    <CheckCircle className="w-5 h-5 mr-2" />
                  ) : (
                    <AlertTriangle className="w-5 h-5 mr-2" />
                  )}
                  <span className="font-medium">{formStatus.success ? "Success!" : "Error!"}</span>
                </div>
                <p>{formStatus.message}</p>
                {formStatus.success && (
                  <button 
                    onClick={() => setFormStatus({submitted: false, success: false, message: ""})}
                    className="mt-4 text-primary hover:underline"
                  >
                    Submit another inquiry
                  </button>
                )}
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-accent-800 mb-1"
                  >
                    {t('contact.form.name')}
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-accent-200 rounded-md focus:ring-2 focus:ring-primary focus:border-primary transition"
                    required
                  />
                </div>
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-accent-800 mb-1"
                  >
                    {t('contact.form.email')}
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-accent-200 rounded-md focus:ring-2 focus:ring-primary focus:border-primary transition"
                    required
                  />
                </div>
                <div>
                  <label
                    htmlFor="phone"
                    className="block text-sm font-medium text-accent-800 mb-1"
                  >
                    {t('contact.form.phone')}
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-accent-200 rounded-md focus:ring-2 focus:ring-primary focus:border-primary transition"
                  />
                </div>
                <div>
                  <label
                    htmlFor="subject"
                    className="block text-sm font-medium text-accent-800 mb-1"
                  >
                    {t('contact.form.subject')}
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-accent-200 rounded-md focus:ring-2 focus:ring-primary focus:border-primary transition"
                    required
                  />
                </div>
                <div>
                  <label
                    htmlFor="inquiryType"
                    className="block text-sm font-medium text-accent-800 mb-1"
                  >
                    {t('contact.form.inquiryType.label')}
                  </label>
                  <select
                    id="inquiryType"
                    name="inquiryType"
                    value={formData.inquiryType}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-accent-200 rounded-md focus:ring-2 focus:ring-primary focus:border-primary transition"
                    required
                  >
                    <option value="">{t('contact.form.inquiryType.placeholder')}</option>
                    <option value="general">{t('contact.form.inquiryType.general')}</option>
                    <option value="careers">{t('contact.form.inquiryType.careers')}</option>
                    <option value="partnership">{t('contact.form.inquiryType.partnership')}</option>
                    <option value="media">{t('contact.form.inquiryType.media')}</option>
                    <option value="other">{t('contact.form.inquiryType.other')}</option>
                  </select>
                </div>
                <div>
                  <label
                    htmlFor="message"
                    className="block text-sm font-medium text-accent-800 mb-1"
                  >
                    {t('contact.form.message')}
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows="4"
                    className="w-full p-3 border border-accent-200 rounded-md focus:ring-2 focus:ring-primary focus:border-primary transition"
                    required
                  ></textarea>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-100 shadow-sm mb-4">
                  <label className="flex items-start cursor-pointer group">
                    <div className="relative flex items-center">
                      <input
                        type="checkbox"
                        name="terms"
                        checked={formData.terms}
                        onChange={(e) => setFormData({...formData, terms: e.target.checked})}
                        className="form-checkbox h-5 w-5 text-primary focus:ring-primary rounded border-gray-300 transition-all group-hover:border-primary"
                        required
                      />
                    </div>
                    <span className="ml-3 text-sm text-gray-700">
                      I agree to the {' '}
                      <Link to="/terms" className="text-primary hover:text-primary-dark underline font-medium">
                        Terms and Conditions
                      </Link>{' '}
                      and {' '}
                      <Link to="/privacy" className="text-primary hover:text-primary-dark underline font-medium">
                        Privacy Policy
                      </Link>.
                    </span>
                  </label>
                </div>
                <p className="text-sm text-gray-600">
                  {t('contact.form.required')}
                </p>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full ${
                    isSubmitting ? 'bg-gray-400' : 'bg-primary hover:bg-primary-dark'
                  } text-white px-6 py-3 rounded-md transition-colors flex items-center justify-center font-medium shadow-md hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200`}
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="mr-2" size={18} />
                      {t('contact.form.submit')}
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Contact;
